import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { loginUser, clearRedirectUrl, getUserProfile } from '../../store/slices/authSlice';
import { setLoading } from '../../store/slices/uiSlice';
import { Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';

const LoginForm = ({ onSignUpClick, onForgotPasswordClick, onClose }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });

  const [errors, setErrors] = useState({});
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading } = useAppSelector((state) => state.ui);
  const { redirectUrl, user, twofa_type, twofa_questions, twofa_email, twofa_password, twofa_phone } = useAppSelector((state) => state.auth);
  const [showPassword, setShowPassword] = useState(false);

  // Handle redirection after successful login
  useEffect(() => {
    // Check if 2FA is required first
    if (twofa_type && twofa_email) {
      console.log('🔐 2FA required - redirecting to 2FA verification page');
      // Store 2FA data in sessionStorage for TwoFactorVerifyPage
      sessionStorage.setItem('twofa_email', twofa_email);
      if (twofa_questions && twofa_questions.length > 0) {
        sessionStorage.setItem('twofa_questions', JSON.stringify(twofa_questions));
      }
      if (twofa_phone) {
        sessionStorage.setItem('twofa_phone', twofa_phone);
      }
      
      const method = twofa_type === 'security_que' ? 'security_questions' : twofa_type;
      navigate(`/2fa-verify?method=${method}`);
      onClose();
      return;
    }

    if (redirectUrl && user) {
      console.log('🔄 LoginForm: Handling login redirection:', {
        userType: user.user_type,
        redirectUrl: redirectUrl,
        isAuthenticated: true,
        tokenInLocalStorage: localStorage.getItem('token') ? 'YES' : 'NO'
      });

      // Check if there's a stored redirect URL from protected route or flight selection
      const storedRedirectUrl = sessionStorage.getItem('redirectAfterLogin');

      // All redirections are now internal - determine target URL based on user type
      let targetUrl;
      
      if (user.user_type === 'Admin' || user.user_type === 'SubAdmin') {
        // Admin users go to admin dashboard
        targetUrl = '/admin';
      } else if (user.user_type === 'Airliner') {
        // Airliner always goes to their profile page
        targetUrl = '/airliner/profile';
      } else if (user.user_type === 'JetSetter') {
        // JetSetter stays on the same page if there's a stored URL, otherwise goes to profile
        targetUrl = storedRedirectUrl || '/jetsetter/profile/user-details';
      } else {
        // Fallback for other user types
        targetUrl = redirectUrl || storedRedirectUrl || '/jetsetter/profile/user-details';
      }
      // Clear stored redirect URL
      sessionStorage.removeItem('redirectAfterLogin');

      console.log('⏱️ LoginForm: Waiting 200ms before navigation to ensure token is set...');
      
      // Increased delay to ensure Redux state and axios defaults are fully updated
      setTimeout(() => {
        console.log('🚀 LoginForm: Navigating to:', targetUrl);
        navigate(targetUrl);
        onClose();
      }, 200);

      // Clear the redirect URL after handling
      dispatch(clearRedirectUrl());
    }
  }, [redirectUrl, user, navigate, onClose, dispatch]);

  // Redirect to 2FA verification page when 2FA is required
  useEffect(() => {
    if (twofa_type === 'security_que' && twofa_questions && twofa_questions.length > 0) {
      console.log('🔐 2FA Required - Redirecting to security question verification page');
      console.log('📋 Questions:', twofa_questions);
      console.log('📧 Email:', twofa_email);
      
      // Store 2FA data in sessionStorage for the verification page
      sessionStorage.setItem('twofa_email', twofa_email);
      sessionStorage.setItem('twofa_password', twofa_password);
      sessionStorage.setItem('twofa_questions', JSON.stringify(twofa_questions));
      
      // Close the login modal first
      onClose();
      
      // Then redirect to 2FA security question verification page
      setTimeout(() => {
        navigate('/2fa-verify');
      }, 100);
    }
  }, [twofa_type, twofa_questions, twofa_email, twofa_password, navigate, onClose]);

  // Redirect to 2FA verification page when OTP 2FA is required
  useEffect(() => {
    if ((twofa_type === 'email' || twofa_type === 'sms') && twofa_email) {
      console.log('🔐 2FA Required - Redirecting to OTP verification page');
      console.log('📧 Email:', twofa_email);
      console.log('📱 Phone:', twofa_phone);
      
      // Store 2FA data in sessionStorage for the verification page
      sessionStorage.setItem('twofa_email', twofa_email);
      sessionStorage.setItem('twofa_password', twofa_password);
      sessionStorage.setItem('twofa_phone', twofa_phone || '');
      
      // Close the login modal first
      onClose();
      
      // Then redirect to 2FA OTP verification page
      setTimeout(() => {
        navigate(`/2fa-verify?method=${twofa_type}`);
      }, 100);
    }
  }, [twofa_type, twofa_email, twofa_password, twofa_phone, navigate, onClose]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.email.trim()) {
      errors.email = 'Please enter a User ID';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.password.trim()) {
      errors.password = 'Please provide a password';
    } else if (formData.password.length < 5) {
      errors.password = 'Your password must be at least 5 characters long';
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    // Clear errors if validation passes
    setErrors({});

    try {
      dispatch(setLoading(true));

      // Dispatch login action - backend determines user type and redirect URL
      const result = await dispatch(loginUser({
        email: formData.email,
        password: formData.password,
        rememberMe: formData.rememberMe
      })).unwrap();

      // Check if 2FA is required
      if (result.requires2FA) {
        // 2FA is required, don't fetch profile yet
        // The SecurityQuestionModal will be shown by useEffect
        return;
      }

      // Show success toast
      toast.success(`Welcome back, ${result.user.first_name}!`);

      // Fetch user profile immediately after login to load profile picture and complete user data
      await dispatch(getUserProfile()).unwrap();

      // Redirection will be handled by useEffect when redirectUrl is set

    } catch (error) {
      // Check if account is inactive
      if (error.isInactive || (error.message && error.message.toLowerCase().includes('inactive'))) {
        toast.error('Your account is inactive. Please send an activation request to the admin.');
      } else {
        // Show error toast
        toast.error(error || 'Login failed. Please check your credentials and try again.');
      }

    } finally {
      dispatch(setLoading(false));
    }
  };

  return (
    <div className="max-w-md mx-auto p-6">
      <h2 className="text-2xl font-bold text-jet-gray-900 mb-2 text-center">Login</h2>
      <p className="text-jet-gray-600 text-sm mb-6 text-center leading-relaxed">
        Sign in to access your personalized profile and your reservations.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="form-group">
          <input
            type="email"
            name="email"
            placeholder="EMAIL OR USER ID"
            value={formData.email}
            onChange={handleChange}
            style={{ textTransform: 'uppercase' }}
            className="w-full px-4 py-3 border border-jet-gray-300 rounded-md text-sm bg-white text-jet-gray-800 placeholder:text-jet-gray-400 transition-colors duration-300 focus:outline-none focus:border-jet-yellow focus:shadow-[0_0_0_2px_rgba(255,215,0,0.2)]"
          />
          {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
        </div>

        <div className="form-group relative">
          <input
            type={showPassword ? 'text' : 'password'}
            name="password"
            placeholder="Password"
            value={formData.password}
            onChange={handleChange}
            className="password-field w-full px-4 py-3 pr-12 border border-jet-gray-300 rounded-md text-sm bg-white text-jet-gray-800 placeholder:text-jet-gray-400 transition-colors duration-300 focus:outline-none focus:border-jet-yellow focus:shadow-[0_0_0_2px_rgba(255,215,0,0.2)]"
          />

          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-jet-gray-500 hover:text-jet-gray-700 transition-colors"
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
          {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
        </div>

        <div className="flex items-center py-2">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              name="rememberMe"
              checked={formData.rememberMe}
              onChange={handleChange}
              className="w-4 h-4 text-jet-yellow border-jet-gray-300 rounded focus:ring-jet-yellow focus:ring-2 cursor-pointer"
            />
            <span className="ml-2 text-sm text-jet-gray-600">Remember me</span>
          </label>
        </div>

        <div className="flex justify-between items-center py-2">
          <button type="button" onClick={onSignUpClick} className="text-jet-gray-600 text-sm hover:text-jet-gray-800 transition-colors duration-200 bg-none border-none cursor-pointer p-0">
            Sign Up
          </button>
          <button type="button" onClick={onForgotPasswordClick} className="text-jet-gray-600 text-sm hover:text-jet-gray-800 transition-colors duration-200 bg-none border-none cursor-pointer p-0">
            Forgot Password ?
          </button>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-jet-yellow text-jet-gray-900 border-0 py-3 px-6 rounded-md text-sm font-semibold cursor-pointer transition-all duration-300 hover:bg-jet-yellow-hover hover:-translate-y-0.5 focus:outline-none focus:shadow-[0_0_0_2px_rgba(255,215,0,0.4)] mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'LOGGING IN...' : 'LOGIN'}
        </button>
      </form>
    </div>
  );
};

export default LoginForm;